import React, { useState, useEffect } from 'react';
import { Settings, Utensils, Clock, Layout, Save, Plus, Trash2, XCircle, Ticket, Award, Percent, ChevronRight, Gift, ChevronDown, ChevronUp, GripVertical } from 'lucide-react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import initialMenuData from '../data/menu.json';
import { DataService } from '../services/dataService';
import './AdminDashboard.css';

const AdminDashboard = () => {
    const [activeTab, setActiveTab] = useState('menu');
    const [data, setData] = useState(null);
    const [saveStatus, setSaveStatus] = useState('');

    useEffect(() => {
        const loadData = async () => {
            const result = await DataService.getAllData(initialMenuData);
            setData(result);
        };
        loadData();
    }, []);

    const handleSave = async () => {
        await DataService.saveData(data);
        setSaveStatus('Sauvegardé avec succès !');
        setTimeout(() => setSaveStatus(''), 3000);
    };

    if (!data) return <div className="admin-page container text-center">Chargement...</div>;

    const tabs = [
        { id: 'menu', label: 'Menu & Carte', icon: Utensils, desc: 'Gérez vos plats et catégories' },
        { id: 'hours', label: 'Horaires', icon: Clock, desc: 'Planning d\'ouverture' },
        { id: 'vouchers', label: 'Codes Promo', icon: Ticket, desc: 'Réductions et offres' },
        { id: 'loyalty', label: 'Fidélité', icon: Award, desc: 'Points et récompenses' },
        { id: 'hero', label: 'Hero Slides', icon: Layout, desc: 'Visuels accueil' },
        { id: 'settings', label: 'Réglages', icon: Settings, desc: 'Infos restaurant' },
    ];

    return (
        <div className="admin-page">
            <div className="container">
                <header className="admin-header">
                    <div className="admin-title-group">
                        <h1>Panel Admin</h1>
                        <p>Contrôlez l'expérience Sugar Hut en temps réel.</p>
                    </div>
                    <button onClick={handleSave} className="save-btn">
                        <Save size={20} /> Enregistrer
                    </button>
                </header>

                {saveStatus && (
                    <div className="bg-green-100 text-green-700 p-4 rounded-3xl mb-12 text-center font-black animate-fade-in border border-green-200">
                        {saveStatus}
                    </div>
                )}

                <div className="admin-layout">
                    <aside className="admin-sidebar">
                        <div className="px-4 py-2 text-[10px] font-black uppercase tracking-widest text-gray-400">Navigation</div>
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`tab-btn ${activeTab === tab.id ? 'tab-btn-active' : 'tab-btn-inactive'}`}
                            >
                                <tab.icon size={22} strokeWidth={activeTab === tab.id ? 2.5 : 2} />
                                <div className="flex flex-col items-start leading-tight">
                                    <span className="text-sm font-black">{tab.label}</span>
                                    <span className="text-[10px] opacity-60 font-medium hidden lg:block">{tab.desc}</span>
                                </div>
                                {activeTab === tab.id && <ChevronRight size={16} className="ml-auto opacity-40" />}
                            </button>
                        ))}
                    </aside>

                    <main className="admin-main">
                        {activeTab === 'menu' && <MenuManager data={data} setData={setData} />}
                        {activeTab === 'hours' && <HoursManager data={data} setData={setData} />}
                        {activeTab === 'vouchers' && <VoucherManager data={data} setData={setData} />}
                        {activeTab === 'loyalty' && <LoyaltyManager data={data} setData={setData} />}
                        {activeTab === 'hero' && <HeroManager data={data} setData={setData} />}
                        {activeTab === 'settings' && <SettingsManager data={data} setData={setData} />}
                    </main>
                </div>
            </div>
        </div>
    );
};

const MenuManager = ({ data, setData }) => {
    const [expandedCats, setExpandedCats] = useState([0]); // First cat expanded by default

    const toggleCat = (idx) => {
        if (expandedCats.includes(idx)) {
            setExpandedCats(expandedCats.filter(i => i !== idx));
        } else {
            setExpandedCats([...expandedCats, idx]);
        }
    };

    const addCategory = () => {
        const newCategory = { category: 'Nouvelle Catégorie', items: [] };
        setData({ ...data, menu: [...data.menu, newCategory] });
        setExpandedCats([...expandedCats, data.menu.length]);
    };

    const addItem = (catIdx) => {
        const newItem = {
            id: Date.now(),
            name: 'Nouveau plat',
            description: 'Description du plat',
            price: 0,
            popular: false,
            outOfStock: false
        };
        const newMenu = [...data.menu];
        newMenu[catIdx].items.push(newItem);
        setData({ ...data, menu: newMenu });
    };

    const removeItem = (catIdx, itemIdx) => {
        const newMenu = [...data.menu];
        newMenu[catIdx].items.splice(itemIdx, 1);
        setData({ ...data, menu: newMenu });
    };

    const handleReorderCategories = (newOrder) => {
        setData({ ...data, menu: newOrder });
    };

    const handleReorderItems = (catIdx, newItems) => {
        const newMenu = [...data.menu];
        newMenu[catIdx].items = newItems;
        setData({ ...data, menu: newMenu });
    };

    return (
        <div className="animate-fade-in">
            <div className="section-header">
                <div className="flex flex-col">
                    <h2 className="text-3xl font-black italic">Gestion de la Carte</h2>
                    <p className="text-sm text-gray-400">Organisez vos catégories et vos produits par glisser-déposer</p>
                </div>
                <button onClick={addCategory} className="save-btn text-sm py-3 px-6">
                    <Plus size={18} /> Catégorie
                </button>
            </div>

            <Reorder.Group axis="y" values={data.menu} onReorder={handleReorderCategories} className="space-y-6">
                {data.menu.map((cat, catIdx) => {
                    const isExpanded = expandedCats.includes(catIdx);
                    return (
                        <Reorder.Item key={cat.category + catIdx} value={cat} className="category-block">
                            <div className="category-header-wrap">
                                <div className="drag-handle">
                                    <GripVertical size={20} />
                                </div>

                                <div className="category-input-wrap" onClick={() => toggleCat(catIdx)}>
                                    <input
                                        className="category-input"
                                        value={cat.category}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                            const newMenu = [...data.menu];
                                            newMenu[catIdx].category = e.target.value;
                                            setData({ ...data, menu: newMenu });
                                        }}
                                    />
                                    <div className="ml-auto flex items-center gap-4">
                                        <span className="text-[10px] font-black bg-gray/10 px-2 py-1 rounded text-gray-400">
                                            {cat.items.length} PRODUITS
                                        </span>
                                        {isExpanded ? <ChevronUp size={20} className="text-primary" /> : <ChevronDown size={20} />}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (window.confirm('Supprimer cette catégorie et tous ses produits ?')) {
                                                    const newMenu = [...data.menu];
                                                    newMenu.splice(catIdx, 1);
                                                    setData({ ...data, menu: newMenu });
                                                }
                                            }}
                                            className="text-gray-300 hover:text-red-500 transition-colors"
                                        >
                                            <Trash2 size={20} />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <AnimatePresence>
                                {isExpanded && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        className="category-content"
                                    >
                                        <Reorder.Group axis="y" values={cat.items} onReorder={(newItems) => handleReorderItems(catIdx, newItems)} className="item-list">
                                            {cat.items.map((item, itemIdx) => (
                                                <Reorder.Item key={item.id} value={item} className="admin-item-card">
                                                    <div className="drag-handle pr-2 border-r border-gray/10">
                                                        <GripVertical size={16} />
                                                    </div>
                                                    <div className="item-inputs">
                                                        <div className="flex flex-col gap-1">
                                                            <label className="text-[10px] font-black uppercase text-gray-400 ml-1">Nom</label>
                                                            <input
                                                                className="admin-input"
                                                                value={item.name}
                                                                onChange={(e) => {
                                                                    const newMenu = [...data.menu];
                                                                    newMenu[catIdx].items[itemIdx].name = e.target.value;
                                                                    setData({ ...data, menu: newMenu });
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="flex flex-col gap-1">
                                                            <label className="text-[10px] font-black uppercase text-gray-400 ml-1">Prix (€)</label>
                                                            <input
                                                                type="number"
                                                                className="admin-input font-black text-primary"
                                                                value={item.price}
                                                                onChange={(e) => {
                                                                    const newMenu = [...data.menu];
                                                                    newMenu[catIdx].items[itemIdx].price = parseFloat(e.target.value);
                                                                    setData({ ...data, menu: newMenu });
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="flex flex-col justify-center gap-2 bg-white p-3 rounded-2xl border border-gray">
                                                            <label className="flex items-center gap-2 text-[10px] font-black cursor-pointer select-none">
                                                                <input
                                                                    type="checkbox"
                                                                    className="w-3 h-3 accent-primary"
                                                                    checked={item.popular}
                                                                    onChange={(e) => {
                                                                        const newMenu = [...data.menu];
                                                                        newMenu[catIdx].items[itemIdx].popular = e.target.checked;
                                                                        setData({ ...data, menu: newMenu });
                                                                    }}
                                                                /> POPULAIRE
                                                            </label>
                                                            <label className="flex items-center gap-2 text-[10px] font-black cursor-pointer select-none text-red-500">
                                                                <input
                                                                    type="checkbox"
                                                                    className="w-3 h-3 accent-red-500"
                                                                    checked={item.outOfStock}
                                                                    onChange={(e) => {
                                                                        const newMenu = [...data.menu];
                                                                        newMenu[catIdx].items[itemIdx].outOfStock = e.target.checked;
                                                                        setData({ ...data, menu: newMenu });
                                                                    }}
                                                                /> ÉPUISÉ
                                                            </label>
                                                        </div>
                                                        <div className="md:col-span-3 flex flex-col gap-1">
                                                            <textarea
                                                                className="admin-input h-16 text-xs leading-relaxed"
                                                                value={item.description}
                                                                onChange={(e) => {
                                                                    const newMenu = [...data.menu];
                                                                    newMenu[catIdx].items[itemIdx].description = e.target.value;
                                                                    setData({ ...data, menu: newMenu });
                                                                }}
                                                                placeholder="Description..."
                                                            />
                                                        </div>
                                                    </div>
                                                    <button onClick={() => removeItem(catIdx, itemIdx)} className="text-gray-300 hover:text-red-500 p-2">
                                                        <Trash2 size={18} />
                                                    </button>
                                                </Reorder.Item>
                                            ))}
                                            <button onClick={() => addItem(catIdx)} className="add-btn py-4">
                                                <Plus size={18} /> Ajouter un produit
                                            </button>
                                        </Reorder.Group>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </Reorder.Item>
                    );
                })}
            </Reorder.Group>
        </div>
    );
};

const VoucherManager = ({ data, setData }) => {
    const addVoucher = () => {
        const newVoucher = { code: 'NOUVEAU', discount: 10, type: 'percentage', active: true };
        setData({ ...data, vouchers: [...(data.vouchers || []), newVoucher] });
    };

    const removeVoucher = (idx) => {
        const newVouchers = [...data.vouchers];
        newVouchers.splice(idx, 1);
        setData({ ...data, vouchers: newVouchers });
    };

    return (
        <div className="animate-fade-in">
            <div className="section-header">
                <div className="flex flex-col">
                    <h2 className="text-3xl font-black italic">Smart-Vouchers</h2>
                    <p className="text-sm text-gray-400 font-medium">Créez et gérez vos codes de réduction</p>
                </div>
                <button onClick={addVoucher} className="save-btn text-sm py-3 px-6">
                    <Plus size={18} /> Nouveau Code
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {(data.vouchers || []).map((v, idx) => (
                    <div key={idx} className="voucher-card">
                        <div className="voucher-header">
                            <input
                                className="voucher-code-input"
                                value={v.code}
                                onChange={(e) => {
                                    const newV = [...data.vouchers];
                                    newV[idx].code = e.target.value.toUpperCase();
                                    setData({ ...data, vouchers: newV });
                                }}
                                placeholder="CODE_PROMO"
                            />
                            <button onClick={() => removeVoucher(idx)} className="text-white/40 hover:text-red-400 ml-4 transition-colors">
                                <Trash2 size={20} />
                            </button>
                        </div>

                        <div className="voucher-body">
                            <div className="voucher-value-badge">
                                <div className="flex-1">
                                    <label className="text-[10px] font-black uppercase text-gray-400 block mb-1">Valeur</label>
                                    <input
                                        type="number"
                                        className="admin-input w-full font-black text-2xl p-0 h-auto border-none bg-transparent"
                                        value={v.discount}
                                        onChange={(e) => {
                                            const newV = [...data.vouchers];
                                            newV[idx].discount = parseFloat(e.target.value);
                                            setData({ ...data, vouchers: newV });
                                        }}
                                    />
                                </div>
                                <div className="w-[1px] h-10 bg-gray/20" />
                                <div className="flex-1">
                                    <label className="text-[10px] font-black uppercase text-gray-400 block mb-1">Type</label>
                                    <select
                                        className="admin-input w-full font-bold p-0 h-auto border-none bg-transparent cursor-pointer"
                                        value={v.type}
                                        onChange={(e) => {
                                            const newV = [...data.vouchers];
                                            newV[idx].type = e.target.value;
                                            setData({ ...data, vouchers: newV });
                                        }}
                                    >
                                        <option value="percentage">% de remise</option>
                                        <option value="fixed">€ fixe</option>
                                    </select>
                                </div>
                            </div>

                            <div
                                onClick={() => {
                                    const newV = [...data.vouchers];
                                    newV[idx].active = !v.active;
                                    setData({ ...data, vouchers: newV });
                                }}
                                className={`status-toggle-card ${v.active ? 'active' : 'inactive'}`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`w-3 h-3 rounded-full ${v.active ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                                    <span className="font-black text-xs uppercase tracking-widest">
                                        {v.active ? 'Code Activé' : 'Code Désactivé'}
                                    </span>
                                </div>
                                <div className={`w-12 h-6 rounded-full relative transition-colors duration-300 ${v.active ? 'bg-green-500' : 'bg-gray-200'}`}>
                                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all duration-300 ${v.active ? 'left-7' : 'left-1'}`} />
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const LoyaltyManager = ({ data, setData }) => {
    const loyalty = data.restaurant.loyalty;

    return (
        <div className="animate-fade-in">
            <h2 className="text-3xl font-black italic mb-8">Programme Fidélité</h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-white p-8 rounded-[40px] border border-gray shadow-lg">
                    <div className="flex items-center gap-4 mb-10">
                        <div className="p-4 bg-primary/10 rounded-3xl">
                            <Award className="text-primary" size={32} />
                        </div>
                        <div>
                            <h3 className="text-xl font-black italic">Paramètres des Points</h3>
                            <p className="text-sm text-gray-400">Règles de gain et paliers</p>
                        </div>
                    </div>

                    <div className="space-y-8">
                        <div>
                            <label className="text-[10px] font-black uppercase text-gray-400 mb-2 block">Gain par Euro (€)</label>
                            <div className="flex items-center gap-4">
                                <input
                                    type="number"
                                    className="admin-input w-24 text-center font-black text-2xl"
                                    value={loyalty.pointsPerEuro}
                                    onChange={(e) => {
                                        const newData = { ...data };
                                        newData.restaurant.loyalty.pointsPerEuro = parseInt(e.target.value);
                                        setData(newData);
                                    }}
                                />
                                <span className="font-bold text-gray-500">Point(s) gagné(s)</span>
                            </div>
                        </div>

                        <div>
                            <label className="text-[10px] font-black uppercase text-gray-400 mb-2 block">Seuil de Récompense</label>
                            <div className="flex items-center gap-4">
                                <input
                                    type="number"
                                    className="admin-input w-36 text-center font-black text-2xl text-primary"
                                    value={loyalty.rewardThreshold}
                                    onChange={(e) => {
                                        const newData = { ...data };
                                        newData.restaurant.loyalty.rewardThreshold = parseInt(e.target.value);
                                        setData(newData);
                                    }}
                                />
                                <span className="font-bold text-gray-500">Points requis</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-dark p-8 rounded-[40px] text-white shadow-2xl relative overflow-hidden">
                    <div className="relative z-10">
                        <div className="flex items-center gap-4 mb-10">
                            <div className="p-4 bg-white/10 rounded-3xl">
                                <Gift size={32} className="text-primary" />
                            </div>
                            <div>
                                <h3 className="text-xl font-black italic">Le Cadeau</h3>
                                <p className="text-sm text-white/40">Visible par vos clients</p>
                            </div>
                        </div>

                        <label className="text-[10px] font-black uppercase text-white/40 mb-2 block">Texte de la récompense</label>
                        <textarea
                            className="w-full bg-white/5 border border-white/10 rounded-2xl p-6 font-bold text-xl h-44 outline-none focus:border-primary transition-all"
                            value={loyalty.rewardText}
                            onChange={(e) => {
                                const newData = { ...data };
                                newData.restaurant.loyalty.rewardText = e.target.value;
                                setData(newData);
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className="admin-loyalty-preview text-center">
                <span className="preview-label">Aperçu Client en Direct</span>
                <div className="loyalty-card-admin-wrapper">
                    <div className="loyalty-card-visual" style={{ margin: '0 auto', maxWidth: '400px' }}>
                        <div className="card-top">
                            <div className="card-brand">
                                <Award className="text-primary" size={24} />
                                <span className="text-sm">FIDÉLITÉ SUGAR HUT</span>
                            </div>
                            <div className="card-chip" />
                        </div>
                        <div className="card-points-display">
                            <span className="points-number text-5xl">42</span>
                            <span className="points-label text-[8px]">Points Cumulés</span>
                        </div>
                        <div className="card-bottom">
                            <div className="card-holder text-[10px]">CLIENT PREMIUM</div>
                        </div>
                    </div>
                    <div className="mt-8 p-6 bg-white rounded-3xl shadow-sm border border-gray inline-block min-w-80">
                        <div className="mb-4 flex justify-between items-center px-2">
                            <span className="text-[10px] font-black">Progression</span>
                            <span className="font-black text-sm">42 / {loyalty.rewardThreshold}</span>
                        </div>
                        <div className="h-3 bg-gray/20 rounded-full overflow-hidden">
                            <div className="h-full bg-primary" style={{ width: `${(42 / loyalty.rewardThreshold) * 100}%` }}></div>
                        </div>
                        <p className="mt-4 text-sm font-bold text-dark italic">"{loyalty.rewardText}"</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const HoursManager = ({ data, setData }) => {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const frDays = {
        monday: 'Lundi', tuesday: 'Mardi', wednesday: 'Mercredi',
        thursday: 'Jeudi', friday: 'Vendredi', saturday: 'Samedi', sunday: 'Dimanche'
    };

    const toggleClosed = (day) => {
        const newData = { ...data };
        const dayData = newData.restaurant.hours[day];
        if (dayData.closed) {
            dayData.closed = false;
        } else {
            dayData.closed = true;
        }
        setData(newData);
    };

    return (
        <div className="animate-fade-in">
            <div className="section-header">
                <div className="flex flex-col">
                    <h2 className="text-3xl font-black italic">Planning d'Ouverture</h2>
                    <p className="text-sm text-gray-400 font-medium">Définissez vos horaires d'accueil hebdomadaires</p>
                </div>
            </div>

            <div className="hours-list">
                {days.map(day => {
                    const isClosed = data.restaurant.hours[day]?.closed;
                    const isWeekend = ['saturday', 'sunday'].includes(day);

                    return (
                        <div key={day} className={`hour-card ${isClosed ? 'is-closed' : ''}`}>
                            <div className="day-info">
                                <span className="day-name">{frDays[day]}</span>
                                <span className={`day-tag ${isWeekend ? 'tag-weekend' : 'tag-weekday'}`}>
                                    {isWeekend ? 'Weekend' : 'Semaine'}
                                </span>
                            </div>

                            <div className="time-slots-group">
                                {!isClosed ? (
                                    <>
                                        <div className="time-input-wrapper">
                                            <label>Ouverture</label>
                                            <input
                                                type="time"
                                                value={data.restaurant.hours[day].open}
                                                onChange={(e) => {
                                                    const newData = { ...data };
                                                    newData.restaurant.hours[day].open = e.target.value;
                                                    setData(newData);
                                                }}
                                                className="scheduler-input"
                                            />
                                        </div>
                                        <span className="flex items-center font-black opacity-20 pt-4 text-xl">➜</span>
                                        <div className="time-input-wrapper">
                                            <label>Fermeture</label>
                                            <input
                                                type="time"
                                                value={data.restaurant.hours[day].close}
                                                onChange={(e) => {
                                                    const newData = { ...data };
                                                    newData.restaurant.hours[day].close = e.target.value;
                                                    setData(newData);
                                                }}
                                                className="scheduler-input"
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <div className="flex items-center justify-center h-[66px] px-12 bg-gray/10 rounded-2xl border-2 border-dashed border-gray/20">
                                        <span className="text-sm font-black text-gray-400 italic">RESTAURANT FERMÉ</span>
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-end">
                                <div
                                    onClick={() => toggleClosed(day)}
                                    className={`closed-toggle-btn ${isClosed ? 'closed' : 'active'}`}
                                >
                                    {isClosed ? <XCircle size={18} /> : <Clock size={18} />}
                                    <span>{isClosed ? 'FERMÉ' : 'OUVERT'}</span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const HeroManager = ({ data, setData }) => {
    return (
        <div className="animate-fade-in">
            <h2 className="text-3xl font-black italic mb-8">Slider de l'Accueil</h2>
            <div className="hero-editor">
                {data.heroSlides.map((slide, idx) => (
                    <div key={slide.id} className="hero-slide-edit">
                        <div className="hero-img-preview group">
                            <div className="hero-order-badge">#{idx + 1}</div>
                            <img src={slide.image} alt={slide.title} />
                            <div className="change-img-overlay">
                                <button className="change-btn">Remplacer l'image</button>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="text-[10px] font-black uppercase text-gray-400 ml-1">Grand Titre</label>
                                <input
                                    className="admin-input w-full font-black text-xl"
                                    value={slide.title}
                                    onChange={(e) => {
                                        const newData = { ...data };
                                        newData.heroSlides[idx].title = e.target.value;
                                        setData(newData);
                                    }}
                                />
                            </div>
                            <div>
                                <label className="text-[10px] font-black uppercase text-gray-400 ml-1">Sous-texte accrocheur</label>
                                <textarea
                                    className="admin-input w-full h-24 text-sm font-medium leading-relaxed"
                                    value={slide.subtitle}
                                    onChange={(e) => {
                                        const newData = { ...data };
                                        newData.heroSlides[idx].subtitle = e.target.value;
                                        setData(newData);
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const SettingsManager = ({ data, setData }) => (
    <div className="animate-fade-in">
        <h2 className="text-3xl font-black italic mb-8">Réglages Généraux</h2>
        <div className="category-block max-w-2xl">
            <div className="flex flex-col gap-8">
                <div className="flex flex-col gap-2">
                    <label className="text-[10px] font-black uppercase text-gray-400 ml-1">Nom du restaurant</label>
                    <input
                        className="admin-input text-2xl font-black"
                        value={data.restaurant.name}
                        onChange={(e) => {
                            const newData = { ...data };
                            newData.restaurant.name = e.target.value;
                            setData(newData);
                        }}
                    />
                </div>
                <div className="flex flex-col gap-2">
                    <label className="text-[10px] font-black uppercase text-gray-400 ml-1">Slogan publicitaire</label>
                    <textarea
                        className="admin-input h-32 text-lg font-medium leading-relaxed"
                        value={data.restaurant.description}
                        onChange={(e) => {
                            const newData = { ...data };
                            newData.restaurant.description = e.target.value;
                            setData(newData);
                        }}
                    />
                </div>
                <div className="flex flex-col gap-2">
                    <label className="text-[10px] font-black uppercase text-gray-400 ml-1">Adresse physique</label>
                    <input
                        className="admin-input font-bold"
                        value={data.restaurant.address}
                        onChange={(e) => {
                            const newData = { ...data };
                            newData.restaurant.address = e.target.value;
                            setData(newData);
                        }}
                    />
                </div>
            </div>
        </div>
    </div>
);

export default AdminDashboard;
